import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/authContext";

/**
 * Top-bar user chip + sign-out dropdown. Sits next to the Canvas env
 * switcher. Renders nothing until a signed-in user exists (the AuthGuard
 * shows its own splash before that point).
 */
export function UserMenu() {
  const { user, displayName, email, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user) return null;

  return (
    <div className="user-menu" ref={ref}>
      <button
        className="user-pill"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={email ?? displayName ?? "Signed in"}
      >
        <span className="user-avatar" aria-hidden="true">
          {avatarInitial(displayName, email)}
        </span>
        <span className="user-pill-label">
          {shortenName(displayName, email)}
        </span>
        <span className="env-pill-caret">▾</span>
      </button>

      {open && (
        <div className="user-menu-dropdown" role="menu">
          <div className="user-menu-info">
            <div className="user-menu-name">{displayName ?? "Signed in"}</div>
            {email && email !== displayName && (
              <div className="user-menu-email">{email}</div>
            )}
          </div>
          <button
            className="user-menu-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              signOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function avatarInitial(name: string | null, email: string | null): string {
  const src = name ?? email ?? "?";
  return src.trim().charAt(0).toUpperCase() || "?";
}

function shortenName(name: string | null, email: string | null): string {
  // Prefer just the local part of the email so the chip stays narrow even
  // for long display names ("Alex Operator Long Name <aoperator@...>" → "aoperator").
  if (email) return email.split("@")[0] ?? email;
  if (name) return name;
  return "Account";
}
